<template>
  <div class="admin-htmlpages">
    <h1>內文管理</h1>

    <!-- 搜尋列 -->
    <div class="search-bar">
      <input
        v-model="keyword"
        type="text"
        placeholder="輸入關鍵字搜尋"
        @keyup.enter="handleSearch"
      />
      <button class="btn-search" @click="handleSearch">搜尋</button>
    </div>

    <!-- 若有資料：用 table 或 div 列出 htmlpages -->
    <div v-if="htmlpages.length" class="table-container">
      <table>
        <thead>
          <tr>
            <!-- <th style="width: 120px;">HTML 類型</th> -->
            <th style="width: 240px;">標題</th>
            <th>內容 (前 50 字)</th>
            <th style="width: 120px;">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(page, index) in htmlpages" :key="index">
            <!-- <td>{{ page.html_type }}</td> -->
            <!-- 注意：加 data-label 以便手機顯示欄位標題 -->
            <td data-label="標題">{{ page.title }}</td>
            <td data-label="內容">
              {{ page.detail_html.slice(0, 50) }}...
            </td>
            <td data-label="操作">
              <button class="btn-view" @click="editHtml(page)">編輯</button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 分頁區 -->
      <div class="pagination">
        <button
          :disabled="page <= 1"
          @click="goToPage(page - 1)"
        >
          上一頁
        </button>
        <span class="page-info">
          第 {{ page }} / {{ totalPages }} 頁
        </span>
        <button
          :disabled="page >= totalPages"
          @click="goToPage(page + 1)"
        >
          下一頁
        </button>
      </div>
    </div>

    <!-- 若無資料 -->
    <div v-else>
      <p>沒有任何文章資料</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAdminSysTempStore } from '@/store/adminSysTempStore.js'
import axios from 'axios'

const router = useRouter()

// 資料 / 分頁相關
const htmlpages = ref([])
const keyword = ref('')
const limit = ref(30)
const offset = ref(0)
const page = ref(1)
const total = ref(0)
const totalPages = ref(1)

/** 從後端抓取 HTML 頁面 */
async function fetchHtmlPages() {
  try {
    const { data } = await axios.post('/api/admin/htmls', {
      limit: limit.value, // 每頁筆數
      offset: offset.value, // 跳過多少筆 (用於分頁)
      keyword: keyword.value, // 搜尋關鍵字
    })
    if (data.success) {
      htmlpages.value = data.htmlpages // 真正的資料陣列
      total.value = data.total // 總筆數
      totalPages.value = Math.ceil(total.value / limit.value) || 1
    } else {
      console.error(data.message)
    }
  } catch (err) {
    console.error('取得 HTML Pages 資料失敗:', err)
  }
}

/** 搜尋：重置 offset/page 並重新抓取 */
function handleSearch() {
  page.value = 1
  offset.value = 0
  fetchHtmlPages()
}

/** 切換分頁 */
function goToPage(newPage) {
  if (newPage < 1 || newPage > totalPages.value) return
  page.value = newPage
  offset.value = (newPage - 1) * limit.value
  fetchHtmlPages()
}

/** 檢視(編輯)：在新視窗打開該 HTML 頁面 */
function editHtml(page) {
  if (!page.html_type) return
  if (!page.version_page_type) return


  const url = `/editor/?edittype=htmlpage&htmlname=${page.html_type}&vpage=${page.version_page_type}`
    // 移除寬度高度等參數，僅保留 _blank
  window.open(url, '_blank')

  // // 手機板不支援 window.open
  // if (window.innerWidth > 768) {
  //   const url = `/editor/?edittype=htmlpage&htmlname=${page.html_type}&vpage=${page.version_page_type}`
  //   window.open(url, '_blank', 'width=1830,height=1200,scrollbars=yes,resizable=yes')
  // } else {
  //   Swal.fire('手機板不支援編輯 HTML')
  // }
}

onMounted(async () => {
  // 檢查是否登入
  const useAdminSysTemp = useAdminSysTempStore()
  await useAdminSysTemp.fetchAdminCheckLogin()
  if (!useAdminSysTemp.isLoggedIn) {
    router.push('/admin/login')
    return
  }
  console.log('AdminHtmlPagesPage mounted')
  await fetchHtmlPages()
})
</script>

<style scoped>
.admin-htmlpages {
  background-color: #fafafa;
  padding: 1rem;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  max-width: 900px; /* 讓畫面更集中 */
  margin: 0 auto;
}

/* 搜尋欄 */
.search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 1rem;
}

.search-bar input {
  flex: 1;
  padding: 8px;
  font-size: 1rem;
}

.btn-search {
  background-color: #007bff;
  color: #fff;
  padding: 8px 14px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.btn-search:hover {
  background-color: #0056b3;
}

/* 表格樣式 */
.table-container {
  overflow-x: auto; /* 若欄位太多時可橫向捲動 */
}

table {
  width: 100%;
  border-collapse: collapse;
  background-color: #fff;
}

thead {
  background-color: #f2f2f2;
}

th,
td {
  text-align: left;
  padding: 12px;
  border: 1px solid #e0e0e0;
}

/* 分頁區 */
.pagination {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 12px;
}

.pagination button {
  background-color: #2e3a59;
  color: #fff;
  padding: 6px 12px;
  border: none;
  cursor: pointer;
  border-radius: 4px;
}

.pagination button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.page-info {
  font-weight: bold;
}

/* 檢視按鈕 */
.btn-view {
  padding: 6px 12px;
  border: none;
  background-color: #28a745;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
}

.btn-view:hover {
  background-color: #218838;
}

/* ----------------------------------
   手機RWD (max-width: 768px)
   - 參考 Stacked Table
---------------------------------- */
@media (max-width: 768px) {
  table thead {
    display: none; /* 隱藏表頭 */
  }

  table,
  table tbody,
  table tr,
  table td {
    display: block;
    width: 100%;
  }

  table tr {
    margin-bottom: 1rem;
    border-bottom: 2px solid #eee; /* 分隔每筆 */
  }

  /* 讓每個 td 自帶一行 label */
  table td {
    border: none;
    border-bottom: 1px solid #eee;
    position: relative;
    padding-left: 40%;
    min-height: 40px;
    box-sizing: border-box;
    white-space: pre-wrap;
  }

  /* 用 ::before 顯示 data-label (欄位名稱) */
  table td::before {
    content: attr(data-label);
    position: absolute;
    left: 12px;
    width: 35%;
    font-weight: bold;
    white-space: nowrap;
  }

  /* 內容置中/對齊可依需求調整 */
  table td {
    text-align: left;
  }
}
</style>
